// Design tokens for the emma chat widget — 1:1 mirror of the DS canonical
// source: bff/static/design-system/tokens.css.
//
// Per CLAUDE.md (root + design-system/CLAUDE.md), the DS is the SINGLE
// source of truth for all visual decisions. The legacy monolith
// (`static/index.html` :host block) had its own divergences (e.g.
// `--white-100`, `--letter-spacing-snug`, `--blue-100`, `--z-tooltip` —
// all monolith-only, NOT in DS). The widget aligns with DS, not the
// divergence.
//
// When DS tokens.css changes, update this file in lockstep. There is no
// runtime sync; the diff is a manual chore (just like the monolith's
// `:host` block per DS CLAUDE.md "Связь с продакшном" section).
//
// Usage in a Lit component:
//
//   import { designTokens } from './design-tokens.js';
//
//   class MyEl extends LitElement {
//     static styles = [designTokens, css`
//       .label { color: var(--color-text-muted); padding: var(--space-3); }
//     `];
//   }

import { css } from 'lit';

export const designTokens = css`
  :host {
    /* --- Typography: families --- */
    --font-sans: 'Onest', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;

    /* --- Typography: weights --- */
    --font-regular: 400;
    --font-medium: 500;
    --font-semibold: 600;
    --font-bold: 700;

    /* --- Typography: sizes (fluid) --- */
    --text-2xs: 0.625rem; /* 10px — labels, badges */
    --text-xs: 0.6875rem; /* 11px — meta, captions */
    --text-sm: clamp(0.75rem, 0.9vw, 0.8125rem); /* 12–13px — secondary UI */
    --text-md: clamp(0.8125rem, 1vw, 0.875rem); /* 13–14px — body, cards */
    --text-lg: clamp(0.875rem, 1.05vw, 0.9375rem); /* 14–15px — labels */
    --text-xl: clamp(1rem, 1.5vw, 1.125rem); /* 16–18px — section heads */
    --text-hero: clamp(2rem, 5vw, 3.375rem); /* 32–54px — hero title */

    /* --- Typography: leading (line-height) --- */
    --leading-none: 1; /* buttons, badges — single line */
    --leading-tight: 1.2; /* headings, hero */
    --leading-snug: 1.35; /* subheadings, compact UI */
    --leading-md: 1.55; /* body, cards — default */
    --leading-loose: 1.75; /* long-form reading */

    /* --- Typography: tracking (letter-spacing) --- */
    --tracking-tight: -0.022em; /* large headings, hero */
    --tracking-normal: 0em; /* body — browser default */
    --tracking-wide: 0.04em; /* small labels, chips */
    --tracking-wider: 0.08em; /* uppercase section titles */

    /* --- Colors: neutrals --- */
    --white: #ffffff;
    --gray-03: hsl(222, 20%, 97.5%);
    --gray-05: hsl(222, 18%, 96%);
    --gray-07: hsl(222, 17%, 93.5%);
    --gray-10: hsl(222, 16%, 91%);
    --gray-15: hsl(222, 14%, 87%);
    --gray-20: hsl(222, 12%, 82%);
    --gray-40: hsl(222, 10%, 66%);
    --gray-50: hsl(222, 10%, 54%);
    --gray-60: hsl(222, 12%, 42%);
    --gray-80: hsl(222, 16%, 26%);
    --gray-100: hsl(222, 22%, 12%);

    /* --- Colors: brand blue --- */
    --blue-03: hsl(225, 100%, 98.5%);
    --blue-15: hsl(225, 80%, 88%);
    --blue-30: hsl(225, 72%, 76%);
    --blue-60: hsl(225, 70%, 54%);
    --blue-80: hsl(225, 72%, 42%);
    --blue-accent: hsl(225, 83%, 56%); /* CTA blue — sits outside the lightness scale */

    /* --- Colors: green --- */
    --green-03: hsl(142, 60%, 97%);
    --green-15: hsl(142, 50%, 86%);
    --green-60: hsl(142, 48%, 40%);
    --green-80: hsl(142, 52%, 28%);

    /* --- Colors: semantic --- */
    --color-success: var(--green-60); /* checkmark green */
    --color-warning: #f6a61f; /* AWS orange */
    --color-text: var(--gray-100);
    --color-text-muted: var(--gray-60);
    --color-text-subtle: var(--gray-40);
    --color-border: var(--gray-10);
    --color-bg: var(--gray-03);
    --color-surface: var(--white);

    /* --- Borders & radii --- */
    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 16px;
    --radius-full: 9999px;

    /* --- Spacing scale --- */
    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 20px;
    --space-6: 24px;
    --space-8: 32px;
    --space-10: 40px;

    /* --- Shadows & Effects --- */
    --shadow-card: 0 4px 24px hsla(225, 20%, 60%, 0.12),
      0 0 48px hsla(220, 15%, 85%, 0.3); /* main widget card */
    --shadow-avatar: drop-shadow(0 0 8px rgba(48, 83, 247, 0.18)); /* emma logo — animate only */

    /* --- Motion --- */
    --duration-fast: 150ms;
    --duration-normal: 250ms;
    --duration-slow: 400ms; /* layout transitions: expand/reveal, send button width */
    --duration-collapse: 180ms; /* accordions: faster feels snappier */
    --duration-arrival: 300ms; /* element arrives: checkmark scale+fade */
    --duration-pulse: 450ms; /* badge pulse: scale 1→1.2→1 */
    --easing: cubic-bezier(0.4, 0, 0.2, 1); /* standard: hover, color, on-place */
    --easing-out: cubic-bezier(0, 0, 0.2, 1); /* decelerate: elements entering */
    --easing-spring: cubic-bezier(0.34, 1.56, 0.64, 1); /* overshoot: arrival moments */

    /* --- Breakpoints (reference only — use as raw px in @media) ---
       --bp-sm:  480px
       --bp-md:  768px
       --bp-lg:  1024px
       --bp-xl:  1280px
       --bp-2xl: 1920px
    */
  }
`;
